import type { TranslateFormDraftContentPayload } from 'src/graphql';
import type { QuestionData } from '../types';
import { ANSWER_TYPES } from '../types';
import { mergePartialQuestionDataForLocale } from './localizedQuestionFieldHelpers';

function hasTrimmed(s: string | null | undefined): boolean {
  return Boolean(s?.trim());
}

export function mergeFormTranslateDraftContentIntoQuestions(
  questions: QuestionData[],
  editingLocale: string,
  payload: TranslateFormDraftContentPayload,
): QuestionData[] {
  const byOrder = new Map(payload.questions.map((q) => [q.orderIndex, q]));

  return questions.map((storageQ) => {
    const tr = byOrder.get(storageQ.order);
    if (!tr) {
      return storageQ;
    }

    const partial: Partial<QuestionData> = {};
    if (hasTrimmed(tr.questionText)) {
      partial.questionText = tr.questionText;
    }
    if (hasTrimmed(tr.description)) {
      partial.description = tr.description ?? '';
    }

    if (storageQ.options?.length && tr.options?.length) {
      const allOptionsTranslated = storageQ.options.every((_, idx) => {
        const translated = tr.options?.find((o) => o.optionOrder === idx);
        return hasTrimmed(translated?.optionText);
      });
      if (allOptionsTranslated) {
        // For data grid questions, store translations AND update display text
        if (storageQ.questionType === ANSWER_TYPES.DATA_GRID) {
          partial.options = storageQ.options.map((opt, idx) => {
            const translatedOption = tr.options!.find(
              (o) => o.optionOrder === idx,
            );
            const translatedLabel = translatedOption?.optionText;

            if (!translatedLabel) {
              return opt;
            }

            // Keep existing translations for other languages, add/update current language
            const existingTranslations = opt.translations || [];
            const filteredTranslations = existingTranslations.filter(
              (t) => t.languageCode !== editingLocale,
            );

            return {
              ...opt,
              // Update display text to show translation in UI
              text: translatedLabel,
              // Also store in translations array for backend save
              translations: [
                ...filteredTranslations,
                {
                  languageCode: editingLocale,
                  optionText: translatedLabel,
                },
              ],
            };
          });
        } else {
          // For single/multiple choice, update the text directly (existing behavior)
          partial.options = storageQ.options.map((opt, idx) => ({
            ...opt,
            text: tr.options!.find((o) => o.optionOrder === idx)!.optionText,
          }));
        }
      }
    }

    if (
      partial.questionText === undefined &&
      partial.description === undefined &&
      partial.options === undefined
    ) {
      return storageQ;
    }

    return {
      ...storageQ,
      ...mergePartialQuestionDataForLocale(storageQ, editingLocale, partial),
    };
  });
}
